import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api, { coursesAPI, assignmentsAPI } from '../services/api';
import Button from '../components/ui/Button';
import Card, { StatCard } from '../components/ui/Card';
import Badge from '../components/ui/Badge';
import Spinner from '../components/ui/Spinner';
import EmptyState from '../components/ui/EmptyState';
import Breadcrumbs from '../components/ui/Breadcrumbs';
import Modal from '../components/ui/Modal';
import Input from '../components/ui/Input';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';

const CourseStats = () => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const { user } = useAuth();
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Atribuiri individuale (pentru anularea celor neîncepute — plasă de siguranță „am greșit destinatarul")
  const [assignments, setAssignments] = useState([]);
  const [revokingId, setRevokingId] = useState(null);

  // SUPER_ADMIN bulk reset
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetLoadingList, setResetLoadingList] = useState(false);
  const [resetTrainings, setResetTrainings] = useState([]);
  const [resetSelected, setResetSelected] = useState(new Set());
  const [resetReason, setResetReason] = useState('');
  const [resetSearch, setResetSearch] = useState('');
  const [resetting, setResetting] = useState(false);
  const isSuperAdmin = user?.role === 'SUPER_ADMIN';

  const openResetModal = async () => {
    setShowResetModal(true);
    setResetSelected(new Set());
    setResetReason('');
    setResetSearch('');
    setResetLoadingList(true);
    try {
      const res = await api.get(`/courses/${courseId}/admin-trainings`);
      setResetTrainings(res.data || []);
    } catch (err) {
      showToast(err.response?.data?.error || t('recovery.bulkResetLoadError'), 'error');
      setShowResetModal(false);
    } finally {
      setResetLoadingList(false);
    }
  };

  const toggleSelect = (id) => {
    const next = new Set(resetSelected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setResetSelected(next);
  };

  const filteredTrainings = resetTrainings.filter((t) => {
    if (!resetSearch.trim()) return true;
    const q = resetSearch.toLowerCase();
    return (
      `${t.employee.firstName} ${t.employee.lastName}`.toLowerCase().includes(q) ||
      (t.employee.email || '').toLowerCase().includes(q) ||
      (t.employee.company?.name || '').toLowerCase().includes(q)
    );
  });

  const toggleAllFiltered = () => {
    const allIds = filteredTrainings.map((t) => t.trainingId);
    const allSelected = allIds.every((id) => resetSelected.has(id));
    const next = new Set(resetSelected);
    if (allSelected) allIds.forEach((id) => next.delete(id));
    else allIds.forEach((id) => next.add(id));
    setResetSelected(next);
  };

  const handleBulkReset = async () => {
    if (resetSelected.size === 0) {
      showToast(t('recovery.bulkResetErrorNone'), 'error');
      return;
    }
    setResetting(true);
    try {
      const res = await api.post(`/courses/${courseId}/admin-reset-trainings`, {
        trainingIds: Array.from(resetSelected),
        reason: resetReason,
      });
      showToast(res.data.message || t('recovery.bulkResetConfirm'), 'success');
      setShowResetModal(false);
      fetchStats();
    } catch (err) {
      showToast(err.response?.data?.error || t('recovery.bulkResetError'), 'error');
    } finally {
      setResetting(false);
    }
  };

  useEffect(() => {
    fetchStats();
    fetchAssignments();
  }, [courseId]);

  const fetchStats = async () => {
    try {
      const response = await coursesAPI.getStats(courseId);
      setData(response.data);
    } catch (err) {
      console.error(err);
      showToast(t('courseStats.loadError'), 'error');
      navigate('/courses');
    } finally {
      setLoading(false);
    }
  };

  // Atribuirile individuale ale cursului (best-effort — secțiune auxiliară statisticilor).
  const fetchAssignments = async () => {
    try {
      const res = await coursesAPI.getAssignments(courseId);
      setAssignments(res.data || []);
    } catch (err) {
      setAssignments([]);
    }
  };

  // Anulează o atribuire neîncepută. Backend-ul refuză (409) dacă instruirea a fost deja începută.
  const handleRevoke = async (assignmentId) => {
    setRevokingId(assignmentId);
    try {
      await assignmentsAPI.revoke(assignmentId);
      showToast(t('courseStats.assignmentRevoked'), 'success');
      setAssignments((prev) => prev.filter((a) => a.id !== assignmentId));
      fetchStats(); // reîmprospătează contoarele (Total Atribuiri etc.)
    } catch (err) {
      showToast(err.response?.data?.error || t('courseStats.revokeError'), 'error');
    } finally {
      setRevokingId(null);
    }
  };

  if (loading) return <Spinner label={t('courseStats.loadingStats')} />;

  if (!data)
    return (
      <EmptyState
        icon={
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
            />
          </svg>
        }
        title={t('courseStats.noDataTitle')}
        description={t('courseStats.noDataDesc')}
      />
    );

  const hasTest = data.hasTest;
  const { stats } = data;
  const unsignedCount = stats.completed - (stats.signed || 0);

  return (
    <div className="space-y-6">
      <Breadcrumbs
        items={[
          { label: t('courses.title'), href: '/courses' },
          { label: data.course.title || t('courseStats.courseFallback') },
          { label: t('courses.stats') },
        ]}
      />
      <div>
        <h1 className="page-title">{data.course.title}</h1>
        <div className="flex items-center gap-2 mt-1">
          <Badge color={hasTest ? 'blue' : 'yellow'}>
            {hasTest ? t('courseStats.withTest') : t('courseStats.withoutTest')}
          </Badge>
          <span className="text-sm text-slate-500 dark:text-slate-400">
            {t('courseStats.materialsCount', { count: data.course.modules?.length || 0 })}
          </span>
        </div>
      </div>

      {/* Statistici principale */}
      <div
        className={`grid grid-cols-1 sm:grid-cols-2 ${hasTest ? 'lg:grid-cols-5' : 'lg:grid-cols-4'} gap-4`}
      >
        <StatCard
          label={t('courseStats.totalAssignments')}
          value={stats.totalAssigned}
          icon={
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
              />
            </svg>
          }
        />

        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-500 dark:text-slate-400">
                {t('courseStats.completed')}
              </p>
              <p className="mt-1 text-2xl font-semibold text-accent-600 dark:text-accent-300 tabular-nums">
                {stats.completed}
              </p>
              <p className="mt-1 text-xs text-slate-400 dark:text-slate-400">
                {hasTest ? t('courseStats.passes') : t('courseStats.completedTrainings')}
              </p>
            </div>
            <div className="w-10 h-10 rounded-lg bg-accent-50 dark:bg-accent-950/50 flex items-center justify-center text-accent-600 dark:text-accent-300 flex-shrink-0">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-500 dark:text-slate-400">
                {t('courseStats.signed')}
              </p>
              <p className="mt-1 text-2xl font-semibold text-primary-600 dark:text-primary-300 tabular-nums">
                {stats.signed || 0}
              </p>
              <p className="mt-1 text-xs text-slate-400 dark:text-slate-400">
                {unsignedCount > 0 ? (
                  <span className="text-warning-600 dark:text-warning-300">
                    {t('courseStats.unsignedCount', { count: unsignedCount })}
                  </span>
                ) : (
                  t('courseStats.allSigned')
                )}
              </p>
            </div>
            <div className="w-10 h-10 rounded-lg bg-primary-50 dark:bg-primary-950/50 flex items-center justify-center text-primary-600 dark:text-primary-300 flex-shrink-0">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                />
              </svg>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-500 dark:text-slate-400">
                {t('common.status.inProgress')}
              </p>
              <p className="mt-1 text-2xl font-semibold text-warning-600 dark:text-warning-300 tabular-nums">
                {stats.inProgress}
              </p>
              <p className="mt-1 text-xs text-slate-400 dark:text-slate-400">
                {t('courseStats.inProgressDesc')}
              </p>
            </div>
            <div className="w-10 h-10 rounded-lg bg-warning-50 dark:bg-warning-950/50 flex items-center justify-center text-warning-600 dark:text-warning-300 flex-shrink-0">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
          </div>
        </Card>

        {hasTest && (
          <Card>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500 dark:text-slate-400">
                  {t('courseStats.passRate')}
                </p>
                <p className="mt-1 text-2xl font-semibold text-primary-600 dark:text-primary-300 tabular-nums">
                  {stats.passRate}%
                </p>
                <p className="mt-1 text-xs text-slate-400 dark:text-slate-400">
                  {t('courseStats.passRateDesc')}
                </p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-primary-50 dark:bg-primary-950/50 flex items-center justify-center text-primary-600 dark:text-primary-300 flex-shrink-0">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                  />
                </svg>
              </div>
            </div>
          </Card>
        )}
      </div>

      {/* Scor mediu / Rata completare */}
      <div className="bg-surface-secondary rounded-xl border-2 border-primary-200 dark:border-primary-700 p-6">
        <div className="flex items-center justify-between">
          {hasTest ? (
            <>
              <div>
                <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                  {t('courseStats.avgScore')}
                </h3>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
                  {t('courseStats.avgScoreDesc')}
                </p>
              </div>
              <p className="text-5xl font-bold text-primary-600 dark:text-primary-300 tabular-nums">
                {stats.averageScore}%
              </p>
            </>
          ) : (
            <>
              <div>
                <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                  {t('courseStats.completionRate')}
                </h3>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
                  {t('courseStats.completionRateDesc')}
                </p>
              </div>
              <p className="text-5xl font-bold text-primary-600 dark:text-primary-300 tabular-nums">
                {stats.totalAssigned > 0
                  ? Math.round((stats.completed / stats.totalAssigned) * 100)
                  : 0}
                %
              </p>
            </>
          )}
        </div>
      </div>

      {/* Warning for unsigned trainings */}
      {unsignedCount > 0 && (
        <Card className="border-warning-200 dark:border-warning-700 bg-warning-50 dark:bg-warning-950/50">
          <div className="flex items-start gap-3">
            <svg
              className="w-5 h-5 text-warning-600 dark:text-warning-300 flex-shrink-0 mt-0.5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
              />
            </svg>
            <div>
              <p className="font-medium text-warning-800 dark:text-warning-300">
                {unsignedCount === 1
                  ? t('courseStats.unsignedWarningSingular', { count: unsignedCount })
                  : t('courseStats.unsignedWarningPlural', { count: unsignedCount })}
              </p>
              <p className="text-sm text-warning-700 dark:text-warning-300 mt-0.5">
                {t('courseStats.unsignedDesc')}
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Atribuiri pe firme */}
      {data.assignmentsByCompany?.length > 0 && (
        <Card>
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4 flex items-center gap-2">
            <svg
              className="w-5 h-5 text-slate-400 dark:text-slate-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
              />
            </svg>
            {t('courseStats.assignmentsByCompany')}
          </h2>
          <div className="space-y-3">
            {data.assignmentsByCompany.map((item, idx) => {
              const completionRate =
                item.assigned > 0 ? Math.round((item.completed / item.assigned) * 100) : 0;
              return (
                <div
                  key={idx}
                  className="flex items-center gap-4 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg"
                >
                  <div className="flex-1">
                    <p className="font-medium text-slate-900 dark:text-slate-100 text-sm">
                      {item.company?.name || t('courseStats.unknownCompany')}
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      {t('courseStats.completedOfAssigned', {
                        completed: item.completed,
                        assigned: item.assigned,
                      })}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-24 bg-slate-200 dark:bg-slate-700 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${completionRate === 100 ? 'bg-accent-500' : completionRate > 50 ? 'bg-primary-500' : 'bg-warning-500'}`}
                        style={{ width: `${completionRate}%` }}
                      />
                    </div>
                    <span
                      className={`text-sm font-semibold w-10 text-right ${completionRate === 100 ? 'text-accent-600 dark:text-accent-300' : 'text-slate-700 dark:text-slate-300'}`}
                    >
                      {completionRate}%
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Atribuiri trimise — listă individuală; cele neîncepute pot fi anulate (plasă de siguranță). */}
      <Card>
        <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-1 flex items-center gap-2">
          <svg
            className="w-5 h-5 text-slate-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
            />
          </svg>
          {t('courseStats.sentAssignments')}
        </h2>
        <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
          {t('courseStats.revokeHint')}
        </p>

        {assignments.length === 0 ? (
          <p className="text-slate-500 dark:text-slate-400 text-center py-8">
            {t('courseStats.noAssignments')}
          </p>
        ) : (
          <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1 custom-scrollbar">
            {assignments.map((a) => {
              const notStarted = !a.training;
              const status = a.training?.status;
              let badge;
              if (notStarted) badge = <Badge color="yellow">{t('common.status.notStarted')}</Badge>;
              else if (status === 'PASSED')
                badge = <Badge color="green">{t('courseStats.finalized')}</Badge>;
              else if (status === 'FAILED')
                badge = <Badge color="red">{t('courseStats.failed')}</Badge>;
              else if (status === 'IN_PROGRESS')
                badge = <Badge color="blue">{t('common.status.inProgress')}</Badge>;
              else badge = <Badge color="yellow">{t('common.status.notStarted')}</Badge>;
              return (
                <div
                  key={a.id}
                  className="flex items-center justify-between gap-3 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg"
                >
                  <div className="min-w-0">
                    <p className="font-medium text-sm text-slate-900 dark:text-slate-100 truncate">
                      {a.employee?.user?.firstName} {a.employee?.user?.lastName}
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                      {a.employee?.company?.name}
                      {a.dueDate
                        ? ` ${t('courseStats.dueTerm', { date: new Date(a.dueDate).toLocaleDateString('ro-RO') })}`
                        : ''}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {badge}
                    {notStarted && (
                      <Button
                        variant="outline"
                        size="sm"
                        loading={revokingId === a.id}
                        className="text-danger-700 border-danger-300 hover:bg-danger-50 dark:hover:bg-danger-900/30"
                        onClick={() => handleRevoke(a.id)}
                      >
                        {t('common.cancel')}
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Angajati */}
      <Card>
        <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <svg
            className="w-5 h-5 text-slate-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
            />
          </svg>
          {t('courseStats.employeesCompleted')}
        </h2>

        {data.topEmployees.length === 0 ? (
          <p className="text-slate-500 dark:text-slate-400 text-center py-8">
            {t('courseStats.noEmployeesCompleted')}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-800">
                  <th className="table-header">#</th>
                  <th className="table-header">{t('common.name')}</th>
                  {hasTest && <th className="table-header">{t('courseStats.score')}</th>}
                  <th className="table-header">{t('common.date')}</th>
                  <th className="table-header">{t('courseStats.signature')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {data.topEmployees.map((training, index) => {
                  const hasSigned = training.signatures && training.signatures.length > 0;
                  return (
                    <tr key={training.id} className="table-row">
                      <td className="table-cell font-bold text-slate-400 dark:text-slate-400">
                        {index + 1}
                      </td>
                      <td className="table-cell text-slate-900 dark:text-slate-100 font-medium">
                        {training.employee.user.firstName} {training.employee.user.lastName}
                      </td>
                      {hasTest && (
                        <td className="table-cell">
                          {training.score != null ? (
                            <Badge
                              color={
                                training.score >= 90
                                  ? 'green'
                                  : training.score >= 80
                                    ? 'blue'
                                    : 'yellow'
                              }
                            >
                              {training.score}%
                            </Badge>
                          ) : (
                            <span className="text-slate-400 dark:text-slate-400">—</span>
                          )}
                        </td>
                      )}
                      <td className="table-cell text-slate-500 dark:text-slate-400">
                        {training.completedAt
                          ? new Date(training.completedAt).toLocaleDateString('ro-RO')
                          : '—'}
                      </td>
                      <td className="table-cell">
                        {hasSigned ? (
                          <Badge color="green">{t('courseStats.signedBadge')}</Badge>
                        ) : (
                          <Badge color="red">{t('courseStats.unsignedBadge')}</Badge>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Actiuni */}
      <div className="flex flex-col sm:flex-row gap-4">
        <Button
          onClick={() => navigate(`/courses/${courseId}/content`)}
          size="lg"
          className="flex-1"
        >
          <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
            />
          </svg>
          {t('courseStats.manageContent')}
        </Button>
        <Button
          variant="secondary"
          size="lg"
          onClick={() => navigate(`/courses/${courseId}/edit`)}
          className="flex-1"
        >
          <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
            />
          </svg>
          {t('courseStats.editCourse')}
        </Button>
      </div>

      {isSuperAdmin && (
        <Card>
          <h3 className="text-sm font-bold text-amber-700 dark:text-amber-300 uppercase tracking-wider mb-2">
            {t('recovery.sectionLabel')}
          </h3>
          <p className="text-sm text-slate-600 dark:text-slate-400 mb-3">
            {t('recovery.bulkResetSectionDesc')}
          </p>
          <Button
            variant="outline"
            size="md"
            className="text-danger-700 border-danger-400 hover:bg-danger-50 dark:hover:bg-danger-900/30"
            onClick={openResetModal}
          >
            {t('recovery.bulkResetBtn')}
          </Button>
        </Card>
      )}

      {isSuperAdmin && (
        <Modal
          open={showResetModal}
          onClose={() => !resetting && setShowResetModal(false)}
          closable={!resetting}
          title={t('recovery.bulkResetTitle')}
          size="2xl"
          footer={
            <div className="flex gap-3 justify-between items-center w-full">
              <span className="text-sm text-slate-500 dark:text-slate-400">
                {t('recovery.bulkResetSelectedCount', {
                  count: resetSelected.size,
                  total: filteredTrainings.length,
                })}
              </span>
              <div className="flex gap-3">
                <Button
                  variant="secondary"
                  onClick={() => setShowResetModal(false)}
                  disabled={resetting}
                >
                  {t('common.cancel')}
                </Button>
                <Button
                  variant="danger"
                  onClick={handleBulkReset}
                  loading={resetting}
                  disabled={resetting || resetSelected.size === 0}
                >
                  {t('recovery.bulkResetConfirm')}
                </Button>
              </div>
            </div>
          }
        >
          <div className="space-y-4">
            <Input
              label={t('recovery.bulkResetSearch')}
              value={resetSearch}
              onChange={(e) => setResetSearch(e.target.value)}
              placeholder={t('recovery.bulkResetSearchPh')}
            />
            <Input
              label={t('recovery.bulkResetReason')}
              value={resetReason}
              onChange={(e) => setResetReason(e.target.value)}
              placeholder={t('recovery.bulkResetReasonPh')}
            />
            {resetLoadingList ? (
              <Spinner label={t('recovery.bulkResetLoading')} />
            ) : filteredTrainings.length === 0 ? (
              <p className="text-sm text-slate-500 dark:text-slate-400 text-center py-4">
                {t('recovery.bulkResetEmpty')}
              </p>
            ) : (
              <div className="border border-slate-200 dark:border-slate-700 rounded-lg max-h-80 overflow-y-auto">
                <div className="sticky top-0 bg-slate-50 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-3 py-2 flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={
                      filteredTrainings.length > 0 &&
                      filteredTrainings.every((t) => resetSelected.has(t.trainingId))
                    }
                    onChange={toggleAllFiltered}
                    className="w-4 h-4"
                  />
                  <span className="text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                    {t('recovery.bulkResetSelectAll')}
                  </span>
                </div>
                <ul className="divide-y divide-slate-100 dark:divide-slate-800">
                  {filteredTrainings.map((tr) => (
                    <li
                      key={tr.trainingId}
                      className="px-3 py-2 flex items-center gap-3 hover:bg-slate-50 dark:hover:bg-slate-800"
                    >
                      <input
                        type="checkbox"
                        checked={resetSelected.has(tr.trainingId)}
                        onChange={() => toggleSelect(tr.trainingId)}
                        className="w-4 h-4 flex-shrink-0"
                      />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">
                          {tr.employee.firstName} {tr.employee.lastName}
                        </p>
                        <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                          {tr.employee.email} · {tr.employee.company?.name || '—'}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <Badge
                          color={
                            tr.status === 'PASSED'
                              ? 'green'
                              : tr.status === 'FAILED'
                                ? 'red'
                                : tr.status === 'IN_PROGRESS'
                                  ? 'yellow'
                                  : 'gray'
                          }
                        >
                          {tr.status}
                        </Badge>
                        {tr.hasCertificate && (
                          <Badge color="blue" className="text-[10px]">
                            {t('courseStats.certificate')}
                          </Badge>
                        )}
                        {tr.score != null && (
                          <span className="text-xs text-slate-500">{tr.score}%</span>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <p className="text-xs text-amber-700 dark:text-amber-300">
              {t('recovery.bulkResetWarn')}
            </p>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default CourseStats;
